import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInbox, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useSupplierInbox } from 'modules/FreeSupplier/useSupplierInbox';
import {
  RequestLineInstallationResolution,
  findSupplierInboxRequest,
  requestCnCodes,
  resolveRequestInstallationState,
} from 'modules/FreeSupplier/supplierInboxPresentation';
import { buildSupplierRequestReviewPath } from 'modules/FreeSupplier/routes';
import { CgSkeletonBar } from 'modules/Common/ui/CgSkeletons';
import useCnGoodLabel from '../state/useCnGoodLabel';
import { useMockData } from '../state/MockDataContext';
import SupplierTemplateUploadFlow from '../components/SupplierTemplateUploadFlow';

// Request-tailored template upload (/supplier/requests/:requestId/upload). A
// thin route wrapper (B3) — owns its own chrome (back link + request-context
// banner + heading) and renders the SAME dropzone/result flow
// (SupplierTemplateUploadFlow) TemplateUploadPage renders, rather than
// nesting that whole other route page inside this one.
//
// R1 (S7 fix batch 3): a request-scoped upload must target the requested
// installation, not whatever a multi-site supplier happens to leave selected
// — drafts are permanently keyed by the upload's installationId, and
// fulfilment only matches the exact requested id. `resolveRequestInstallationState`
// reduces the request's lines to a single tri-state: `matched` preselects and
// LOCKS the installation choice (passed through to SupplierTemplateUploadFlow
// / CommsTemplateDropzone as read-only context); `unresolvable_scoped` blocks
// the upload flow outright — same treatment as direct entry's unresolvable
// line — since a substitute installation can never fulfil a scoped request;
// an unscoped/ambiguous request leaves the existing free picker untouched.
export default function RequestUploadPage() {
  const { t } = useTranslation('freeSupplier');
  const { requestId } = useParams();
  const inbox = useSupplierInbox();
  const { installationDocs } = useMockData();
  const cnGoodLabel = useCnGoodLabel();
  const match = findSupplierInboxRequest(inbox.data?.items || [], requestId);
  const resolution = match
    ? resolveRequestInstallationState(match.request, installationDocs)
    : null;
  const blocked = resolution?.state === RequestLineInstallationResolution.UnresolvableScoped;
  const lockedInstallation =
    resolution?.state === RequestLineInstallationResolution.Matched
      ? resolution.installation
      : null;

  // Item B1: same fix as TemplateUploadPage — the page header (back link +
  // request-context banner + title) stays flush-left/full-width like every
  // other page; only SupplierTemplateUploadFlow's own card/list content
  // centers itself, via its own inner `mx-auto` widths.
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-5">
        <Link
          to={buildSupplierRequestReviewPath(requestId)}
          className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('freeSupplier:requestUpload.backToRequest', 'Back to request')}
        </Link>

        {/* Item 8: distinguish a query ERROR from a genuinely missing request —
            only the latter falls back to the reassuring "upload anyway" copy;
            an error offers a retry instead of implying the request doesn't
            exist. A still-loading inbox shows neither banner. */}
        {inbox.isError ? (
          <div className="rounded-2xl border border-dashed border-red-300 p-4 flex items-center justify-between gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-red-300"
              />
              {t('freeSupplier:requestUpload.loadError', 'We couldn’t load this request.')}
            </span>
            <CgRadixButton
              variant="outline"
              size="sm"
              onClick={() => inbox.refetch()}
            >
              {t('freeSupplier:requestUpload.retry', 'Retry')}
            </CgRadixButton>
          </div>
        ) : match ? (
          <div className="rounded-2xl border border-primaryColor/20 bg-primaryColor/5 p-4">
            <p className="text-xs font-semibold text-slate-700 m-0">
              {t(
                'freeSupplier:requestUpload.answeringRequest',
                'Answering {{declarantName}}’s request',
                {
                  declarantName: match.connection.declarant.name,
                },
              )}
            </p>
            <p className="text-[11px] text-slate-500 m-0 mt-1">
              {t(
                'freeSupplier:requestUpload.reportingPeriodCn',
                'Reporting period {{period}} · CN {{cnCodes}}',
                {
                  period: match.request.reportingPeriod,
                  cnCodes: requestCnCodes(match.request).map(cnGoodLabel).join(', '),
                },
              )}
            </p>
          </div>
        ) : inbox.isPending ? (
          // A two-line banner, not a page — a full loader stage here would
          // shove the upload dropzone down and then yank it back. Mirror the
          // resolved banner's exact two lines instead.
          <div
            className="rounded-2xl border border-primaryColor/20 bg-primaryColor/5 p-4"
            aria-label={t(
              'freeSupplier:requestUpload.loadingAriaLabel',
              'Loading this data request',
            )}
            role="status"
          >
            <CgSkeletonBar className="h-3 w-56 max-w-full" />
            <CgSkeletonBar className="h-2.5 w-72 max-w-full mt-2" />
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 flex items-center gap-2 text-xs text-slate-500">
            <FontAwesomeIcon
              icon={faInbox}
              className="text-slate-300"
            />
            {t(
              'freeSupplier:requestUpload.notFound',
              'We couldn’t find that request — upload your template anyway and we’ll match it to the goods it contains.',
            )}
          </div>
        )}
      </div>

      <div>
        <h1 className="text-xl font-bold text-slate-800 m-0">
          {t('freeSupplier:requestUpload.title', 'Template Upload')}
        </h1>
        <p className="text-sm text-slate-500 mt-1 m-0">
          {t(
            'freeSupplier:requestUpload.subtitle',
            "Already filled in the EU CBAM communication template? Drop it here — we'll turn it into draft emissions data for you to review before anything is shared.",
          )}
        </p>
      </div>

      {/* R1: a scoped request naming an installation id that no longer
          exists in this workspace can never be fulfilled by uploading
          against a different site — block the flow instead of letting the
          supplier upload drafts that can never satisfy the request. Same
          treatment as RequestDirectEntryPage's unresolvable-scoped-line
          banner. */}
      {blocked ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold text-amber-800 m-0 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t('freeSupplier:requestUpload.blocked.title', 'Can’t fulfil this request')}
          </p>
          <p className="text-[11px] text-amber-700 mt-1 mb-0">
            {t(
              'freeSupplier:requestUpload.blocked.body',
              'The requested installation no longer exists in your workspace — the declarant’s request cannot be fulfilled against a different site. Contact the declarant to update the request.',
            )}
          </p>
        </div>
      ) : (
        <SupplierTemplateUploadFlow lockedInstallation={lockedInstallation} />
      )}
    </div>
  );
}
